// Reusable password strength meter + scoring. Used by the user management form
// and the profile password change. Self-contained heuristic (no external dep):
// length + character-class variety → a 0–4 score. The matching minimum is
// mirrored on the server (services/auth.ts passwordStrengthError).

import { MIN_PASSWORD_LENGTH, STRONG_PASSWORD_LENGTH, MIN_PASSWORD_SCORE } from '../../shared/constants'
import { fill, COMPONENTS_PASSWORD_STRENGTH_METER_TEXTS as T } from '../../shared/texts'

export { MIN_PASSWORD_LENGTH }

export interface PasswordChecks {
    length: boolean
    lower: boolean
    upper: boolean
    digit: boolean
    symbol: boolean
}
export interface PasswordScore {
    score: 0 | 1 | 2 | 3 | 4
    label: string
    checks: PasswordChecks
}

const LABELS = T.levels

export function scorePassword(pw: string): PasswordScore {
    const checks: PasswordChecks = {
        length: pw.length >= MIN_PASSWORD_LENGTH,
        lower: /[a-z]/.test(pw),
        upper: /[A-Z]/.test(pw),
        digit: /\d/.test(pw),
        symbol: /[^A-Za-z0-9]/.test(pw),
    }
    if (!pw) return { score: 0, label: LABELS[0], checks }

    const classes = [checks.lower, checks.upper, checks.digit, checks.symbol].filter(Boolean).length
    let s = 0
    if (pw.length >= MIN_PASSWORD_LENGTH) s++
    if (pw.length >= STRONG_PASSWORD_LENGTH) s++
    if (classes >= 2) s++
    if (classes >= 3) s++
    if (pw.length < MIN_PASSWORD_LENGTH) s = Math.min(s, 1) // too short never ranks above "Schwach"

    const score = Math.max(0, Math.min(4, s)) as PasswordScore['score']
    return { score, label: LABELS[score], checks }
}

// Minimum acceptable strength for setting a password: at least MIN_PASSWORD_LENGTH
// characters and "Mittel" (score ≥ 2). Mirrored on the server.
export function isPasswordAcceptable(pw: string): boolean {
    const { score, checks } = scorePassword(pw)
    return checks.length && score >= MIN_PASSWORD_SCORE
}

export default function PasswordStrengthMeter({ password }: { password: string }) {
    if (!password) return null
    const { score, label, checks } = scorePassword(password)
    const classes = [checks.lower, checks.upper, checks.digit, checks.symbol].filter(Boolean).length
    const ok = isPasswordAcceptable(password)
    return (
        <div className="pw-meter" data-score={score}>
            <div className="pw-meter-bars" role="img" aria-label={fill(T.strength, { label })}>
                {[0, 1, 2, 3].map((i) => (
                    <span key={i} className={`pw-meter-seg${i < score ? ' on' : ''}`} />
                ))}
            </div>
            <div className="pw-meter-label" aria-live="polite">{fill(T.strength, { label })}</div>
            {!ok && (
                <ul className="pw-meter-hints">
                    {!checks.length && <li>{fill(T.minLength, { n: MIN_PASSWORD_LENGTH })}</li>}
                    {classes < 2 && <li>{T.mixClasses}</li>}
                </ul>
            )}
        </div>
    )
}
